<script setup lang="ts">
// The files of an encrypted library, decrypted on this device (HRTH-105.5, 105.6), as
// menus that drill down like an iPod's (HRTH-164.7): Collections, Artists, Albums, Songs,
// Photos, Videos, Files and Recently added, one list of big rows per screen. Where you are
// is in the address (?at=), so Back, the button or the system's, goes up. Albums (audio,
// photo) are one per file; collections are any files, in their order (lib/collections.ts).
// Tapping a file plays or opens it; its ⋯ (or a long-press on a tile) holds the rest.
import { deleteFiles } from '../lib/sealedStorage'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { describeError } from '../api/http'
import type { LibraryRef, PartyMedia, ReportedFile } from '../api/types'
import {
  artistsOf,
  coverOf,
  groupCollections,
  newCollection,
  TYPE_NAMES,
  type Collection,
  type CollectionMeta,
  type CollectionType,
  type OpenCollection,
} from '../lib/collections'
import { createCollection, moveFiles, reorder, saveCollection, setInCollection, uploadCover } from '../lib/collectionOps'
import { entryTitle, fileName, listFolder, matches, sortRows } from '../lib/libraryEntries'
import { decryptBlob, entryRef, forgetEntries, openCollections, openEntries, playableBlob, type OpenEntry } from '../lib/libraryPlay'
import { formatBytes, formatDuration } from '../lib/pattern'
import type { OpenLibrary } from '../sync/engine'
import { usePlayer } from '../stores/player'
import { useSession } from '../stores/session'
import ActionSheet, { type SheetAction } from './ActionSheet.vue'
import CollectionAddModal from './CollectionAddModal.vue'
import CollectionCover from './CollectionCover.vue'
import CollectionEditModal, { type CollectionEdit } from './CollectionEditModal.vue'
import CollectionPicker, { type Pick } from './CollectionPicker.vue'
import EntryEditModal from './EntryEditModal.vue'
import Icon from './Icon.vue'
import LibraryReportModal from './LibraryReportModal.vue'
import MediaRow from './MediaRow.vue'
import PhotoViewer from './PhotoViewer.vue'
import PromptModal from './PromptModal.vue'
import VisualTile from './VisualTile.vue'

const props = defineProps<{ library: OpenLibrary; refresh: number; query: string }>()
const emit = defineEmits<{ empty: [boolean]; share: [LibraryRef]; party: [{ item: LibraryRef; queue: PartyMedia[] }]; search: [string] }>()
const session = useSession()
const player = usePlayer()
const route = useRoute()
const router = useRouter()

const rows = ref<OpenEntry[]>([])
const records = ref<OpenCollection[]>([])
const locked = ref(0)
const loading = ref(false)
const busy = ref<string | null>(null)
const editing = ref<OpenEntry | null>(null)
const reporting = ref<OpenEntry | null>(null)
/** Choosing several files, and which. */
const selecting = ref(false)
const selected = ref<Set<string>>(new Set())
const sheet = ref<{ title: string; subtitle?: string; actions: SheetAction[] } | null>(null)
/** Photos and videos open in the viewer, stepping through the list they were in. */
const viewing = ref<{ photos: OpenEntry[]; at: number; album?: string } | null>(null)
/** Files being put on an album, or added to collections. */
const albuming = ref<OpenEntry[] | null>(null)
const collecting = ref<OpenEntry[] | null>(null)
const editingCollection = ref<Collection<OpenEntry> | null>(null)
const creating = ref<CollectionType | null>(null)

const key = () => session.libraryKey(props.library.id)

async function load(): Promise<void> {
  const k = key()
  loading.value = true
  try {
    forgetEntries(props.library.id)
    const total = (await session.api.libraryEntries(props.library.id)).entries.length
    const files = k ? [...(await openEntries(session.api, k, props.library.id, true)).values()] : []
    const cols = k ? [...(await openCollections(session.api, k, props.library.id)).values()] : []
    rows.value = files
    records.value = cols
    locked.value = total - files.length - cols.length
    emit('empty', total === 0)
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    loading.value = false
  }
}
watch(() => [props.library.id, props.refresh], load, { immediate: true })

// --- Where we are: `at` in the address, `section/rest` (rest may hold slashes: folders).
const at = computed(() => (typeof route.query.at === 'string' ? route.query.at : ''))
const section = computed(() => at.value.split('/')[0] ?? '')
const rest = computed(() => at.value.slice(section.value.length + 1))
function go(to: string): void {
  stopSelecting()
  if (searching.value) emit('search', '')
  void router.push({ query: { ...route.query, at: to || undefined, q: undefined } })
}
/** Up a level: back through history when we came from here, else to the parent. */
function back(): void {
  stopSelecting()
  const parent: Record<string, string> = { artist: 'artists', album: 'albums', collection: 'collections', 'photos-all': 'photos', files: rest.value.includes('/') ? `files/${rest.value.slice(0, rest.value.lastIndexOf('/'))}` : rest.value ? 'files' : '' }
  if ((window.history.state as { back?: string } | null)?.back?.includes('library')) router.back()
  else go(parent[section.value] ?? '')
}

// --- What's in the library.
const isVisual = (r: OpenEntry) => r.meta.kind === 'image' || r.meta.kind === 'video'
const grouped = computed(() => groupCollections(rows.value, records.value))
const audioAlbums = computed(() => grouped.value.albums.filter((a) => a.type === 'audio'))
const photoAlbums = computed(() => grouped.value.albums.filter((a) => a.type === 'visual'))
const songs = computed(() => sortRows(rows.value.filter((r) => r.meta.kind === 'audio'), 'name'))
const artists = computed(() => artistsOf(grouped.value.albums, songs.value))
const allPhotos = computed(() => sortRows(rows.value.filter((r) => r.meta.kind === 'image'), 'added'))
const videos = computed(() => sortRows(rows.value.filter((r) => r.meta.kind === 'video'), 'name'))
const recent = computed(() => sortRows(rows.value, 'added').slice(0, 60))
const albumOf = (r: OpenEntry) => grouped.value.albums.find((c) => c.items.includes(r)) ?? null
const collectionsOf = (r: OpenEntry) => grouped.value.collections.filter((c) => c.items.includes(r))

const MENU = computed(() =>
  [
    { at: 'collections', label: 'Collections', n: grouped.value.collections.length },
    { at: 'artists', label: 'Artists', n: artists.value.length },
    { at: 'albums', label: 'Albums', n: audioAlbums.value.length },
    { at: 'songs', label: 'Songs', n: songs.value.length },
    { at: 'photos', label: 'Photos', n: allPhotos.value.length },
    { at: 'videos', label: 'Videos', n: videos.value.length },
    { at: 'files', label: 'Files', n: rows.value.length },
    { at: 'recent', label: 'Recently added', n: null },
  ].filter((m) => m.n === null || m.n > 0 || m.at === 'collections' || m.at === 'files'),
)

/** The album or collection open, from the address. */
const openCollection = computed(() => {
  if (section.value === 'album') return grouped.value.albums.find((c) => c.id === rest.value) ?? null
  if (section.value === 'collection') return grouped.value.collections.find((c) => c.id === rest.value) ?? null
  return null
})
const openArtist = computed(() => (section.value === 'artist' ? (artists.value.find((a) => a.name === rest.value) ?? null) : null))
const folder = computed(() => (section.value === 'files' ? rest.value : ''))
const here = computed(() => {
  const l = listFolder(rows.value, folder.value)
  return { folders: l.folders, files: sortRows(l.files, 'name') }
})
const crumbs = computed(() => {
  const parts = folder.value.split('/').filter(Boolean)
  return parts.map((name, i) => ({ name, path: parts.slice(0, i + 1).join('/') }))
})

/** The screen's title, under the library's name. */
const title = computed(() => {
  const s = section.value
  if (openCollection.value) return openCollection.value.name
  if (openArtist.value) return openArtist.value.name
  const names: Record<string, string> = {
    collections: 'Collections',
    artists: 'Artists',
    albums: 'Albums',
    songs: 'Songs',
    photos: 'Photos',
    'photos-all': 'All photos',
    videos: 'Videos',
    files: folder.value ? (folder.value.split('/').pop() ?? 'Files') : 'Files',
    recent: 'Recently added',
  }
  return names[s] ?? ''
})

// --- Search: artists, albums, collections and files that match.
const searching = computed(() => props.query.trim().length > 0)
const q = computed(() => props.query.trim().toLowerCase())
const results = computed(() => {
  if (!searching.value) return null
  const has = (s?: string) => !!s && s.toLowerCase().includes(q.value)
  return {
    artists: artists.value.filter((a) => has(a.name)).slice(0, 10),
    albums: grouped.value.albums.filter((a) => has(a.name) || has(a.artist)).slice(0, 20),
    collections: grouped.value.collections.filter((c) => has(c.name)).slice(0, 20),
    files: sortRows(rows.value.filter((r) => matches(r.meta, props.query)), 'name').slice(0, 100),
  }
})

// --- Files: playing, viewing, downloading, deleting.
const ref_ = (r: OpenEntry) => entryRef(props.library.id, r)
const complete = (r: OpenEntry) => r.entry.blobs.some((b) => b.role === 'original')
const canPlay = (r: OpenEntry) => (r.meta.kind === 'audio' || r.meta.kind === 'video') && !!playableBlob(r)
const hasCopy = (r: OpenEntry) => r.entry.blobs.some((b) => b.role === 'stream')
const needsCopy = (r: OpenEntry) => (r.meta.kind === 'audio' || r.meta.kind === 'video') && complete(r) && !playableBlob(r)
const canPreview = (r: OpenEntry) => complete(r) && (r.meta.kind === 'image' || r.meta.mime === 'application/pdf')
const isPlaying = (r: OpenEntry) => player.current?.item.id === r.entry.id
const KIND_LEAD: Record<string, string> = { audio: 'AUD', video: 'VID', image: 'IMG' }
const lead = (r: OpenEntry) => KIND_LEAD[r.meta.kind] ?? (r.meta.mime === 'application/pdf' ? 'PDF' : 'FILE')
const coverFor = (c: Collection<OpenEntry>) => coverOf(c, rows.value)
const GLYPH: Record<CollectionType, string> = { audio: '♪', visual: '▦', files: '▤' }
function collectionLine(c: Collection<OpenEntry>): string {
  const n = c.items.length
  const what = c.type === 'audio' ? `${n} track${n === 1 ? '' : 's'}` : `${n} file${n === 1 ? '' : 's'}`
  return [c.artist, c.release_date?.slice(0, 4), what].filter(Boolean).join(' · ')
}
/** A file's second line: who it's by, or where it is. */
function fileLine(r: OpenEntry): string | undefined {
  const parts = [
    r.meta.kind === 'audio' ? (r.meta.tags?.artist ?? '') : '',
    !complete(r) ? 'incomplete' : needsCopy(r) ? 'no stream' : '',
    r.meta.kind !== 'audio' ? formatBytes(r.meta.size) : '',
  ]
  return parts.filter(Boolean).join(' · ') || undefined
}

function reportedFile(r: OpenEntry): ReportedFile | undefined {
  const blob = r.entry.blobs.find((b) => b.role === 'original')
  const k = r.meta.blobs.original
  return blob && k ? { blob: blob.id, key: k.key, nonce: k.nonce, size: k.size, chunk: k.chunk, mime: r.meta.mime } : undefined
}

async function original(r: OpenEntry): Promise<Blob> {
  const blob = r.entry.blobs.find((b) => b.role === 'original')
  if (!blob || !r.meta.blobs.original) throw new Error('this file hasn’t finished uploading')
  return decryptBlob(session.api, blob.id, r.meta.blobs.original, r.meta.mime)
}

async function guard(id: string, fn: () => Promise<unknown>): Promise<void> {
  busy.value = id
  try {
    await fn()
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = null
  }
}

const download = (r: OpenEntry) =>
  guard(r.entry.id, async () => {
    const href = URL.createObjectURL(await original(r))
    const a = document.createElement('a')
    a.href = href
    a.download = fileName(r.meta.path)
    a.click()
    setTimeout(() => URL.revokeObjectURL(href), 60_000)
  })

function show(r: OpenEntry, among: OpenEntry[] = [r], album?: string): void {
  if (isVisual(r)) {
    const list = among.filter((x) => isVisual(x) && complete(x))
    viewing.value = { photos: list, at: Math.max(0, list.indexOf(r)), ...(album ? { album } : {}) }
    return
  }
  void guard(r.entry.id, async () => {
    const url = URL.createObjectURL(await original(r))
    window.open(url, '_blank', 'noopener')
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  })
}

async function remove(r: OpenEntry): Promise<void> {
  if (!confirm(`Delete ${fileName(r.meta.path)}? This can't be undone.`)) return
  await guard(r.entry.id, async () => {
    // A sealed bucket's files are deleted from here (the server can't reach it).
    await deleteFiles(session.api, props.library.id, r.entry.blobs.map((b) => b.id))
    await session.api.deleteLibraryEntry(r.entry.id)
    await load()
  })
}

function together(tracks: OpenEntry[]): void {
  const playable = tracks.filter(canPlay)
  if (!playable.length) return
  emit('party', { item: ref_(playable[0]!), queue: playable.map((t) => ({ source: 'library', item: ref_(t) })) })
}

/** Plays `r`, queueing the rest of `list` (the album, collection or list it was in). */
function play(r: OpenEntry, list: OpenEntry[], shuffle = false): void {
  void player.play(ref_(r), list.filter(canPlay).map(ref_), { shuffle })
}
function playAll(list: OpenEntry[], shuffle = false): void {
  const playable = list.filter(canPlay)
  const first = shuffle ? playable[Math.floor(Math.random() * playable.length)] : playable[0]
  if (first) play(first, playable, shuffle)
}

/** Screens of photos and videos, where videos open in the viewer among them (else the player). */
const visualScreen = computed(() => section.value === 'photos-all' || section.value === 'videos' || openCollection.value?.type === 'visual')

/** What tapping a file does: choose it, view it (photos, videos among them), play, open, or offer. */
function open(r: OpenEntry, list: OpenEntry[], album?: string): void {
  if (selecting.value) return toggleSelected(r)
  if (r.meta.kind === 'video' && visualScreen.value) return show(r, list, album)
  if (canPlay(r)) play(r, list)
  else if (canPreview(r)) show(r, list, album)
  else sheetFor(r)
}

// --- The ⋯ sheet. Actions that open a dialog or remove the file close the viewer first:
// dialogs would open behind it.
const closeViewer = () => (viewing.value = null)

function sheetFor(r: OpenEntry, list?: OpenEntry[]): void {
  const a: SheetAction[] = []
  const media = r.meta.kind === 'audio' || r.meta.kind === 'video'
  const album = albumOf(r)
  const inHere = openCollection.value?.type === 'files' ? openCollection.value : null
  if (complete(r)) {
    if (canPlay(r)) {
      a.push({ label: 'Play', id: 'file-play', run: () => player.play(ref_(r)) })
      a.push({ label: 'Play next', id: 'file-play-next', run: () => player.playNext(ref_(r)) })
      a.push({ label: 'Add to queue', id: 'file-enqueue', run: () => player.enqueue(ref_(r)) })
      a.push({ label: r.meta.kind === 'video' ? 'Watch together' : 'Listen together', id: 'file-together', run: () => together([r]) })
      a.push({ label: 'Share to a chat', id: 'file-share', run: () => emit('share', ref_(r)) })
    }
    if (canPreview(r)) a.push({ label: 'View', id: 'file-preview', run: () => show(r, list ?? [r]) })
    a.push({ label: 'Download', id: 'file-download', run: () => download(r) })
  }
  if (album && !openCollection.value) a.push({ label: `Go to ${album.name}`, id: 'file-go-album', run: () => go(`album/${album.id}`) })
  if (props.library.can_upload) a.push({ label: 'Add to collection…', id: 'file-collect', run: () => (closeViewer(), (collecting.value = [r])) })
  if (inHere?.record?.entry.can_edit) {
    const items = inHere.items
    const i = items.indexOf(r)
    if (i > 0) a.push({ label: 'Move up', id: 'item-up', run: () => guard(r.entry.id, () => reorderIn(inHere, r, -1)) })
    if (i >= 0 && i < items.length - 1) a.push({ label: 'Move down', id: 'item-down', run: () => guard(r.entry.id, () => reorderIn(inHere, r, 1)) })
    a.push({ label: `Remove from ${inHere.name}`, id: 'item-remove', run: () => guard(r.entry.id, () => toggleIn(inHere, [r], false)) })
  }
  if (r.entry.can_edit) {
    if (r.meta.kind === 'audio' || isVisual(r)) {
      a.push({ label: album ? 'Move to another album…' : 'Put on an album…', id: 'file-album', run: () => (closeViewer(), (albuming.value = [r])) })
      if (album) a.push({ label: `Take off ${album.name}`, id: 'file-take-off', run: () => guard(r.entry.id, () => pick([r], { none: true })) })
    }
    a.push({ label: 'Edit details', id: 'file-edit', run: () => (closeViewer(), (editing.value = r)) })
  }
  a.push({ label: 'Report', id: 'file-report', run: () => (closeViewer(), (reporting.value = r)) })
  if (r.entry.can_edit) a.push({ label: 'Delete', id: 'file-delete', danger: true, run: () => (closeViewer(), remove(r)) })
  const cols = collectionsOf(r)
  const notes = [
    album ? `on ${album.name}` : '',
    cols.length ? `in ${cols.map((c) => c.name).join(', ')}` : '',
    !complete(r) ? 'incomplete upload' : '',
    needsCopy(r) ? 'no stream: browsers can’t play it as it is' : '',
    media && r.meta.copy === 'lighter' && hasCopy(r) ? 'has a lighter copy' : '',
    formatBytes(r.meta.size),
  ].filter(Boolean)
  sheet.value = { title: entryTitle(r.meta), subtitle: notes.join(' · '), actions: a }
}

/** Whether this device may change an album or collection (a tag album is made a record). */
const canEditCollection = (c: Collection<OpenEntry>) => (c.record ? c.record.entry.can_edit : !!props.library.can_upload)

function collectionSheet(c: Collection<OpenEntry>): void {
  const playable = c.items.filter(canPlay)
  const a: SheetAction[] = []
  if (playable.length) {
    a.push({ label: c.type === 'visual' ? 'Watch together' : 'Listen together', id: 'collection-together', run: () => together(c.items) })
    a.push({
      label: 'Add to queue',
      id: 'collection-enqueue',
      run: async () => {
        for (const t of playable) await player.enqueue(ref_(t))
      },
    })
  }
  if (canEditCollection(c)) a.push({ label: `Edit ${TYPE_NAMES[c.type].one}`, id: 'collection-edit', run: () => (editingCollection.value = c) })
  if (c.record?.entry.can_edit) a.push({ label: `Delete ${TYPE_NAMES[c.type].one}`, id: 'collection-delete', danger: true, run: () => deleteCollection(c) })
  sheet.value = { title: c.name, subtitle: collectionLine(c), actions: a }
}

// --- Albums and collections: making, editing, deleting, and what's in them.
function needKey(): string {
  const k = key()
  if (!k) throw new Error('This device doesn’t have this library’s key.')
  return k
}

/** A tag album becomes a record (still gathering its tracks) before it's changed. */
async function ensureRecord(c: Collection<OpenEntry>): Promise<OpenCollection> {
  if (c.record) return c.record
  const meta = newCollection(c.type, c.name, c)
  const entry = await createCollection(session.api, needKey(), props.library.id, meta)
  return { entry, meta }
}

/** Puts files on an album (an existing one, a new one, or none). */
async function pick(files: OpenEntry[], p: Pick): Promise<void> {
  const k = needKey()
  let target = ''
  if ('into' in p) target = (await ensureRecord(p.into)).entry.id
  else if ('create' in p) target = (await createCollection(session.api, k, props.library.id, newCollection(p.create, p.name))).id
  await moveFiles(session.api, k, props.library.id, files, target)
  stopSelecting()
  await load()
}

async function toggleIn(c: Collection<OpenEntry>, files: OpenEntry[], on: boolean): Promise<void> {
  if (!c.record) return
  await setInCollection(session.api, needKey(), props.library.id, c.record, files.map((f) => f.entry.id), on)
  await load()
}

async function reorderIn(c: Collection<OpenEntry>, r: OpenEntry, by: -1 | 1): Promise<void> {
  if (!c.record) return
  await reorder(session.api, needKey(), props.library.id, c.record, r.entry.id, by)
  await load()
}

/** A new collection holding `files`. */
async function collectInto(name: string, files: OpenEntry[]): Promise<void> {
  const meta = { ...newCollection('files', name), items: files.map((f) => f.entry.id) }
  await createCollection(session.api, needKey(), props.library.id, meta)
  stopSelecting()
  await load()
}

async function createEmpty(name: string): Promise<void> {
  const type = creating.value
  if (!type) return
  const e = await createCollection(session.api, needKey(), props.library.id, newCollection(type, name))
  await load()
  go(`${type === 'files' ? 'collection' : 'album'}/${e.id}`)
}

async function editCollection(c: Collection<OpenEntry>, edit: CollectionEdit): Promise<void> {
  const k = needKey()
  const rec = await ensureRecord(c)
  const meta: CollectionMeta = { ...rec.meta, ...edit.meta, blobs: { ...rec.meta.blobs } }
  for (const f of ['artist', 'release_date', 'description'] as const) if (!meta[f]) delete meta[f]
  if (edit.autoCover) {
    delete meta.cover
    delete meta.blobs.art
  }
  if (edit.meta.cover) delete meta.blobs.art
  await saveCollection(session.api, k, props.library.id, rec.entry.id, meta)
  if (edit.upload) await uploadCover(session.api, k, props.library.id, { entry: rec.entry, meta }, edit.upload)
  // A tag album open as a page is now a record: follow it there.
  if (section.value === 'album' && rest.value === c.id && c.id !== rec.entry.id) {
    await router.replace({ query: { ...route.query, at: `album/${rec.entry.id}` } })
  }
  await load()
}

async function deleteCollection(c: Collection<OpenEntry>): Promise<void> {
  if (!c.record) return
  const what = c.type === 'files' ? 'Its files stay in the library.' : `Its ${c.items.length} files stay in the library.`
  if (!confirm(`Delete ${c.name}? ${what}`)) return
  await guard(c.id, async () => {
    await deleteFiles(session.api, props.library.id, c.record!.entry.blobs.map((b) => b.id))
    await session.api.deleteLibraryEntry(c.record!.entry.id)
    if (openCollection.value?.id === c.id) back()
    await load()
  })
}

function toggleSelected(r: OpenEntry): void {
  const s = new Set(selected.value)
  if (s.has(r.entry.id)) s.delete(r.entry.id)
  else s.add(r.entry.id)
  selected.value = s
}
function stopSelecting(): void {
  selecting.value = false
  selected.value = new Set()
}
const selectedRows = computed(() => rows.value.filter((r) => selected.value.has(r.entry.id)))
const selectedEditable = computed(() => selectedRows.value.filter((r) => r.entry.can_edit && (r.meta.kind === 'audio' || isVisual(r))))
const photos = computed(() => rows.value.filter((r) => r.meta.kind === 'image' && complete(r)))
/** Screens that list files, where several can be chosen. */
const listsFiles = computed(
  () => !searching.value && (['songs', 'photos-all', 'videos', 'files', 'recent', 'album', 'collection', 'artist'] as string[]).includes(section.value),
)
</script>

<template>
  <section class="px-6 pt-3 pb-6 max-md:px-4" data-testid="library-files">
    <!-- Where we are: back up a level, and what can be done here -->
    <div v-if="at || searching" class="mb-3 flex min-h-11 items-center gap-2">
      <button
        v-if="at && !searching"
        class="-ml-2 flex h-11 w-11 shrink-0 items-center justify-center text-text-secondary hover:text-text-primary"
        aria-label="Back"
        data-testid="library-up"
        @click="back"
      >
        <Icon name="left" :size="6" />
      </button>
      <h2 class="min-w-0 flex-1 truncate text-xl font-light" data-testid="library-screen">{{ searching ? 'Search' : title }}</h2>
      <template v-if="listsFiles && library.can_upload">
        <button v-if="selecting" class="btn-primary max-md:h-11" :disabled="!selectedRows.length" data-testid="selection-collect" @click="collecting = selectedRows">
          Collect · {{ selectedRows.length }}
        </button>
        <button v-if="selecting && selectedEditable.length" class="btn-outline max-md:h-11" data-testid="selection-album" @click="albuming = selectedEditable">Album…</button>
        <button class="btn-outline max-md:h-11" :aria-pressed="selecting" data-testid="select-files" @click="selecting ? stopSelecting() : (selecting = true)">
          {{ selecting ? 'Done' : 'Select' }}
        </button>
      </template>
      <button
        v-if="!searching && library.can_upload && (section === 'collections' || section === 'albums' || section === 'photos')"
        class="btn-outline max-md:h-11"
        data-testid="new-collection"
        @click="creating = section === 'collections' ? 'files' : section === 'albums' ? 'audio' : 'visual'"
      >
        <Icon name="plus" /> {{ section === 'collections' ? 'New collection' : 'New album' }}
      </button>
    </div>
    <p v-if="locked" class="mono mb-3 text-text-muted">🔒 {{ locked }} without a key on this device</p>
    <p v-if="loading && rows.length === 0" class="mono text-text-muted">Decrypting…</p>

    <!-- Search results -->
    <template v-if="results">
      <p v-if="!results.artists.length && !results.albums.length && !results.collections.length && !results.files.length" class="mono text-text-muted">Nothing matches.</p>
      <section v-if="results.artists.length" class="mb-5">
        <h3 class="label">Artists</h3>
        <MediaRow v-for="a in results.artists" :key="a.name" :title="a.name" lead="ART" nav :data-testid="`artist-${a.name}`" @open="go(`artist/${a.name}`)" />
      </section>
      <section v-if="results.albums.length || results.collections.length" class="mb-5">
        <h3 class="label">Albums and collections</h3>
        <MediaRow
          v-for="c in [...results.albums, ...results.collections]"
          :key="c.id"
          :title="c.name"
          :subtitle="`${TYPE_NAMES[c.type].label} · ${collectionLine(c)}`"
          nav
          :data-testid="`collection-row-${c.name}`"
          @open="go(`${c.type === 'files' ? 'collection' : 'album'}/${c.id}`)"
        >
          <template #art><CollectionCover :cover="coverFor(c)" :glyph="GLYPH[c.type]" /></template>
        </MediaRow>
      </section>
      <section v-if="results.files.length">
        <h3 class="label">Files</h3>
        <MediaRow
          v-for="r in results.files"
          :key="r.entry.id"
          :title="entryTitle(r.meta)"
          :subtitle="[r.meta.path.slice(0, r.meta.path.length - fileName(r.meta.path).length), fileLine(r)].filter(Boolean).join(' · ') || undefined"
          :lead="lead(r)"
          :playing="isPlaying(r)"
          :data-testid="`library-file-${fileName(r.meta.path)}`"
          @open="open(r, results.files)"
          @more="sheetFor(r, results.files)"
        />
      </section>
    </template>

    <!-- The menu -->
    <nav v-else-if="!at" class="border-t border-border" aria-label="Library" data-testid="library-menu">
      <MediaRow v-if="player.current" class="md:hidden" :title="`Now Playing`" :subtitle="player.current.item.title" lead="▶" nav data-testid="menu-now-playing" @open="player.expanded = true" />
      <MediaRow
        v-for="m in MENU"
        :key="m.at"
        :title="m.label"
        :detail="m.n === null ? undefined : String(m.n)"
        nav
        :data-testid="`menu-${m.at}`"
        @open="go(m.at)"
      />
    </nav>

    <!-- An album or collection -->
    <template v-else-if="openCollection">
      <div class="mb-5 flex items-end gap-5 max-sm:flex-col max-sm:items-start" :data-testid="`collection-page-${openCollection.name}`">
        <div class="h-40 w-40 shrink-0 overflow-hidden border border-border max-sm:h-48 max-sm:w-48" data-testid="collection-cover">
          <CollectionCover :cover="coverFor(openCollection)" :glyph="GLYPH[openCollection.type]" />
        </div>
        <div class="min-w-0 flex-1">
          <div class="mono text-text-muted uppercase">{{ TYPE_NAMES[openCollection.type].label }}</div>
          <div class="mono text-text-muted" data-testid="collection-line">{{ collectionLine(openCollection) }}</div>
          <p v-if="openCollection.description" class="mt-1 max-w-prose text-sm text-text-secondary">{{ openCollection.description }}</p>
          <div class="mt-3 flex flex-wrap items-center gap-2">
            <template v-if="openCollection.items.some(canPlay) && openCollection.type !== 'visual'">
              <button class="btn-primary max-md:h-11" data-testid="album-play" @click="playAll(openCollection.items)"><Icon name="play" /> Play</button>
              <button class="btn-outline max-md:h-11" data-testid="album-shuffle" @click="playAll(openCollection.items, true)"><Icon name="shuffle" /> Shuffle</button>
            </template>
            <button
              v-if="openCollection.items.some((i) => isVisual(i) && complete(i)) && openCollection.type !== 'audio'"
              :class="openCollection.type === 'visual' ? 'btn-primary' : 'btn-outline'"
              class="max-md:h-11"
              data-testid="album-view"
              @click="show(openCollection.items.find((i) => isVisual(i) && complete(i))!, openCollection.items, openCollection.name)"
            >
              <Icon name="play" /> View
            </button>
            <button class="btn-icon" :aria-label="`More for ${openCollection.name}`" data-testid="collection-more" @click="collectionSheet(openCollection)">
              <Icon name="more" />
            </button>
          </div>
        </div>
      </div>
      <p v-if="openCollection.items.length === 0" class="mono text-text-muted" data-testid="collection-empty">
        Nothing in it yet. Add files from their ⋯, or several at once with Select.
      </p>
      <div v-if="openCollection.type === 'visual'" class="grid grid-cols-3 gap-0.5 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
        <VisualTile
          v-for="f in openCollection.items"
          :key="f.entry.id"
          :file="f"
          :selecting="selecting"
          :selected="selected.has(f.entry.id)"
          @open="open(f, openCollection.items, openCollection.name)"
          @more="sheetFor(f, openCollection.items)"
        />
      </div>
      <div v-else class="border-t border-border">
        <MediaRow
          v-for="(t, n) in openCollection.items"
          :key="t.entry.id"
          :title="entryTitle(t.meta)"
          :subtitle="openCollection.type === 'audio' ? (t.meta.tags?.artist && t.meta.tags.artist !== openCollection.artist ? t.meta.tags.artist : needsCopy(t) ? 'no stream' : undefined) : fileLine(t)"
          :lead="openCollection.type === 'audio' ? (t.meta.tags?.track ? String(t.meta.tags.track).padStart(2, '0') : '··') : String(n + 1).padStart(2, '0')"
          :detail="t.meta.kind === 'audio' || t.meta.kind === 'video' ? formatDuration(t.meta.tags?.duration) : lead(t)"
          :playing="isPlaying(t)"
          :selecting="selecting"
          :selected="selected.has(t.entry.id)"
          :data-testid="`library-file-${fileName(t.meta.path)}`"
          @open="open(t, openCollection.items, openCollection.name)"
          @more="sheetFor(t, openCollection.items)"
        />
      </div>
    </template>

    <!-- Lists of albums or collections -->
    <template v-else-if="section === 'collections' || section === 'albums' || section === 'photos'">
      <MediaRow v-if="section === 'photos'" title="All photos" :detail="String(allPhotos.length)" lead="▦" nav data-testid="menu-photos-all" @open="go('photos-all')" />
      <MediaRow
        v-for="c in section === 'collections' ? grouped.collections : section === 'albums' ? audioAlbums : photoAlbums"
        :key="c.id"
        :title="c.name"
        :subtitle="collectionLine(c)"
        nav
        :data-testid="`collection-row-${c.name}`"
        @open="go(`${c.type === 'files' ? 'collection' : 'album'}/${c.id}`)"
        @contextmenu.prevent="collectionSheet(c)"
      >
        <template #art><CollectionCover :cover="coverFor(c)" :glyph="GLYPH[c.type]" /></template>
      </MediaRow>
      <p v-if="section === 'collections' && grouped.collections.length === 0" class="mono py-3 text-text-muted">
        No collections yet. Collections hold any files you choose, in your order: tracks, videos, photos, documents. Add one from a file's ⋯, or New collection.
      </p>
    </template>

    <!-- Artists, and one artist -->
    <template v-else-if="section === 'artists'">
      <MediaRow
        v-for="a in artists"
        :key="a.name"
        :title="a.name"
        :subtitle="[a.albums.length ? `${a.albums.length} album${a.albums.length === 1 ? '' : 's'}` : '', `${a.tracks.length} song${a.tracks.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ')"
        lead="ART"
        nav
        :data-testid="`artist-${a.name}`"
        @open="go(`artist/${a.name}`)"
      />
    </template>
    <template v-else-if="openArtist">
      <MediaRow title="Play" lead="▶" no-more data-testid="list-play" @open="playAll(openArtist.tracks)" />
      <MediaRow title="Shuffle" lead="⤨" no-more data-testid="list-shuffle" @open="playAll(openArtist.tracks, true)" />
      <h3 v-if="openArtist.albums.length" class="label mt-5">Albums</h3>
      <MediaRow
        v-for="c in openArtist.albums"
        :key="c.id"
        :title="c.name"
        :subtitle="collectionLine(c)"
        nav
        :data-testid="`collection-row-${c.name}`"
        @open="go(`album/${c.id}`)"
      >
        <template #art><CollectionCover :cover="coverFor(c)" :glyph="GLYPH[c.type]" /></template>
      </MediaRow>
      <h3 class="label mt-5">Songs</h3>
      <MediaRow
        v-for="t in openArtist.tracks"
        :key="t.entry.id"
        :title="entryTitle(t.meta)"
        :subtitle="albumOf(t)?.name"
        lead="♪"
        :detail="formatDuration(t.meta.tags?.duration)"
        :playing="isPlaying(t)"
        :selecting="selecting"
        :selected="selected.has(t.entry.id)"
        :data-testid="`library-file-${fileName(t.meta.path)}`"
        @open="open(t, openArtist.tracks)"
        @more="sheetFor(t)"
      />
    </template>

    <!-- Songs -->
    <template v-else-if="section === 'songs'">
      <MediaRow title="Shuffle songs" lead="⤨" no-more data-testid="list-shuffle" @open="playAll(songs, true)" />
      <MediaRow
        v-for="t in songs"
        :key="t.entry.id"
        :title="entryTitle(t.meta)"
        :subtitle="[t.meta.tags?.artist, albumOf(t)?.name].filter(Boolean).join(' · ') || undefined"
        lead="♪"
        :detail="formatDuration(t.meta.tags?.duration)"
        :playing="isPlaying(t)"
        :selecting="selecting"
        :selected="selected.has(t.entry.id)"
        :data-testid="`library-file-${fileName(t.meta.path)}`"
        @open="open(t, songs)"
        @more="sheetFor(t)"
      />
    </template>

    <!-- All photos, and videos: grids -->
    <template v-else-if="section === 'photos-all' || section === 'videos'">
      <p v-if="(section === 'videos' ? videos : allPhotos).length === 0" class="mono text-text-muted">None yet.</p>
      <div class="grid grid-cols-3 gap-0.5 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
        <VisualTile
          v-for="f in section === 'videos' ? videos : allPhotos"
          :key="f.entry.id"
          :file="f"
          :selecting="selecting"
          :selected="selected.has(f.entry.id)"
          @open="open(f, section === 'videos' ? videos : allPhotos)"
          @more="sheetFor(f, section === 'videos' ? videos : allPhotos)"
        />
      </div>
    </template>

    <!-- Recently added -->
    <template v-else-if="section === 'recent'">
      <MediaRow
        v-for="r in recent"
        :key="r.entry.id"
        :title="entryTitle(r.meta)"
        :subtitle="fileLine(r)"
        :lead="lead(r)"
        :playing="isPlaying(r)"
        :selecting="selecting"
        :selected="selected.has(r.entry.id)"
        :data-testid="`library-file-${fileName(r.meta.path)}`"
        @open="open(r, recent)"
        @more="sheetFor(r, recent)"
      />
    </template>

    <!-- Files: a folder at a time -->
    <template v-else-if="section === 'files'">
      <nav v-if="folder" class="mono mb-2 flex flex-wrap items-center gap-1 text-text-muted" aria-label="Folder">
        <button class="flex h-9 items-center hover:text-text-primary" @click="go('files')">{{ library.name }}</button>
        <template v-for="c in crumbs" :key="c.path">
          <span>/</span>
          <button class="flex h-9 items-center hover:text-text-primary" @click="go(`files/${c.path}`)">{{ c.name }}</button>
        </template>
      </nav>
      <div class="border-t border-border">
        <MediaRow
          v-for="f in here.folders"
          :key="f"
          :title="`${f}/`"
          lead="DIR"
          nav
          :data-testid="`library-folder-${f}`"
          @open="go(`files/${folder ? `${folder}/${f}` : f}`)"
        />
        <MediaRow
          v-for="r in here.files"
          :key="r.entry.id"
          :title="entryTitle(r.meta)"
          :subtitle="fileLine(r)"
          :lead="lead(r)"
          :detail="busy === r.entry.id ? '…' : undefined"
          :playing="isPlaying(r)"
          :selecting="selecting"
          :selected="selected.has(r.entry.id)"
          :data-testid="`library-file-${fileName(r.meta.path)}`"
          @open="open(r, here.files)"
          @more="sheetFor(r, here.files)"
        />
        <p v-if="!loading && here.folders.length === 0 && here.files.length === 0" class="mono py-2 text-text-muted">Nothing here.</p>
      </div>
    </template>

    <ActionSheet v-if="sheet" :title="sheet.title" :subtitle="sheet.subtitle" :actions="sheet.actions" @close="sheet = null" />
    <PhotoViewer v-if="viewing" :library="library.id" :photos="viewing.photos" :start="viewing.at" :album="viewing.album" @close="viewing = null" @more="(r) => sheetFor(r, viewing?.photos)" />
    <CollectionPicker v-if="albuming" :files="albuming" :collections="grouped.albums" :action="(p) => pick(albuming!, p)" @close="albuming = null" />
    <CollectionAddModal
      v-if="collecting"
      :files="collecting"
      :collections="grouped.collections.filter((c) => c.record?.entry.can_edit)"
      :toggle="(c, on) => toggleIn(c, collecting!, on)"
      :create="(name) => collectInto(name, collecting!)"
      @close="(collecting = null), stopSelecting()"
    />
    <CollectionEditModal
      v-if="editingCollection"
      :collection="editingCollection"
      :photos="photos"
      :action="(e) => editCollection(editingCollection!, e)"
      @close="editingCollection = null"
    />
    <PromptModal
      v-if="creating"
      :title="TYPE_NAMES[creating].new"
      label="Name"
      :placeholder="creating === 'audio' ? 'e.g. Live at the Barn' : creating === 'visual' ? 'e.g. Summer 2026' : 'e.g. Road trip'"
      submit-label="Create"
      :maxlength="200"
      :action="createEmpty"
      @close="creating = null"
    />
    <LibraryReportModal
      v-if="reporting"
      :library="library.id"
      :entry="reporting.entry.id"
      :path="reporting.meta.path"
      :title="reporting.meta.tags?.title"
      :artist="reporting.meta.tags?.artist"
      :file="reportedFile(reporting)"
      @close="reporting = null"
    />
    <EntryEditModal v-if="editing" :library="library.id" :entry="editing.entry" :meta="editing.meta" @saved="load" @close="editing = null" />
  </section>
</template>
